"use client"

import { useState } from "react"
import { FaTrash } from 'react-icons/fa';
import styles from "./css/chronic-condition.module.css"
import { updateConsultation } from "../../../../api/clinic/consultationApi.js";

export default function ChronicCondition({ 
  patientName = "Ryan Morris", 
  consultationId, 
  patientData = {}, 
  initialData,
  onDataChange,
  ensureConsultationExists,
  onSave 
}) {
  const [conditions, setConditions] = useState(
    initialData?.conditions || [{ description: "" }]
  )
  const [medications, setMedications] = useState(
    initialData?.medications || [{ drug: "", dosage: "", units: "", mode: "" }]
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  // Notify parent of data changes
  const notifyChange = () => {
    if (onDataChange) onDataChange()
  }

  const addCondition = () => {
    setConditions([...conditions, { description: "" }])
    notifyChange()
  }

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index))
    notifyChange()
  }

  const addMedication = () => {
    setMedications([...medications, { drug: "", dosage: "", units: "", mode: "" }])
    notifyChange()
  }

  const removeMedication = (index) => {
    setMedications(medications.filter((_, i) => i !== index))
    notifyChange()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError("")
      setSaveSuccess(false)

      // Ensure consultation exists (creates if needed)
      const activeConsultationId = await ensureConsultationExists()

      // Prepare data - send as arrays
      const validMeds = medications.filter(m => m.drug.trim())
      const payload = {
        chronicconditions: conditions.filter(c => c.description.trim()).map(c => c.description),
        chronicmedications: validMeds.map(m => m.drug),
        chronicdosage: validMeds.map(m => m.dosage),
        chronicunits: validMeds.map(m => m.units),
        chronicmode: validMeds.map(m => m.mode),
      }

      await updateConsultation(activeConsultationId, payload)

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      
      // Pass data back to parent
      if (onSave) {
        onSave({ conditions, medications })
      }
    } catch (e) {
      setSaveError(e.message || "Failed to save data")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.patientName}>{patientName}</h2>

      <div className={styles.sectionsContainer}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Chronic Condition</h3>
            <button className={styles.addButton} onClick={addCondition}>
              +
            </button>
          </div>

          <div className={styles.tableHeader}>
            <span className={styles.headerCell}>Conduct Description</span>
          </div>

          {conditions.map((condition, index) => (
            <div key={index} className={styles.tableRow}>
              <input
                type="text"
                className={styles.input}
                value={condition.description}
                onChange={(e) => {
                  const newConditions = [...conditions]
                  newConditions[index].description = e.target.value
                  setConditions(newConditions)
                  notifyChange()
                }}
              />
              <button className={styles.deleteButton} onClick={() => removeCondition(index)}>
                <FaTrash color="red" />
              </button>
            </div>
          ))}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Chronic Medication</h3>
            <button className={styles.addButton} onClick={addMedication}>
              +
            </button>
          </div>

          <div className={styles.tableHeader}>
            <span className={styles.headerCell}>Drug</span>
            <span className={styles.headerCell}>Dosage</span>
            <span className={styles.headerCell}>Units</span>
            <span className={styles.headerCell}>Mode</span>
          </div>

          {medications.map((medication, index) => (
            <div key={index} className={styles.tableRow}>
              <input
                type="text"
                className={styles.input}
                value={medication.drug}
                onChange={(e) => {
                  const newMedications = [...medications]
                  newMedications[index].drug = e.target.value
                  setMedications(newMedications)
                  notifyChange()
                }}
              />
              <input
                type="text"
                className={styles.input}
                value={medication.dosage}
                onChange={(e) => {
                  const newMedications = [...medications]
                  newMedications[index].dosage = e.target.value
                  setMedications(newMedications)
                  notifyChange()
                }}
              />
              <input
                type="text"
                className={styles.input}
                value={medication.units}
                onChange={(e) => {
                  const newMedications = [...medications]
                  newMedications[index].units = e.target.value
                  setMedications(newMedications)
                  notifyChange()
                }}
              />
              <input
                type="text"
                className={styles.input}
                value={medication.mode}
                onChange={(e) => {
                  const newMedications = [...medications]
                  newMedications[index].mode = e.target.value
                  setMedications(newMedications)
                  notifyChange()
                }}
              />
              <button className={styles.deleteButton} onClick={() => removeMedication(index)}>
                <FaTrash color="red" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {saveError && (
        <div className={styles.inlineError}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className={styles.successMessage}>
          Data saved successfully!
        </div>
      )}

      <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  )
}